import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";

interface Role {
  role_id: number;
  role_name: string;
}

interface RolesSelectorProps {
  roles: Role[];
  selectedRoles: number[];
  onSelectionChange: (roles: number[]) => void;
  disabled?: boolean;
  preserveItemOrder?: boolean;
  reorderSelectedOnOutside?: boolean;
}

const RolesSelector: React.FC<RolesSelectorProps> = ({
  roles,
  selectedRoles,
  onSelectionChange,
  disabled = false,
  preserveItemOrder = true,
  reorderSelectedOnOutside = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirstMode, setSelectedFirstMode] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<number[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSearchTerm("");
        if (reorderSelectedOnOutside) {
          setSelectedSnapshot(selectedRoles);
          setSelectedFirstMode(true);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [reorderSelectedOnOutside, selectedRoles]);

  useEffect(() => {
    if (disabled && reorderSelectedOnOutside) {
      setSelectedSnapshot(selectedRoles);
      setSelectedFirstMode(true);
    }
  }, [disabled, reorderSelectedOnOutside, selectedRoles]);

  const filteredRoles = useMemo(() => {
    const query = searchTerm.toLowerCase();

    const matchesQuery = (role: Role) => role.role_name.toLowerCase().includes(query);

    if (selectedFirstMode && reorderSelectedOnOutside) {
      const snapshotSet = new Set(selectedSnapshot);

      if (preserveItemOrder) {
        const selected = roles.filter((role) => snapshotSet.has(role.role_id) && matchesQuery(role));
        const unselected = roles.filter((role) => !snapshotSet.has(role.role_id) && matchesQuery(role));
        return [...selected, ...unselected];
      }

      const selected = roles
        .filter((r) => snapshotSet.has(r.role_id) && matchesQuery(r))
        .sort((a, b) => a.role_name.localeCompare(b.role_name));

      const unselected = roles
        .filter((r) => !snapshotSet.has(r.role_id) && matchesQuery(r))
        .sort((a, b) => a.role_name.localeCompare(b.role_name));

      return [...selected, ...unselected];
    }

    if (preserveItemOrder) {
      return roles.filter((role) => matchesQuery(role));
    }

    const selectedSet = new Set(selectedRoles);

    const selected = roles
      .filter(
        (r) =>
          selectedSet.has(r.role_id) &&
            matchesQuery(r)
      )
      .sort((a, b) => a.role_name.localeCompare(b.role_name));

    const unselected = roles
      .filter(
        (r) =>
          !selectedSet.has(r.role_id) &&
          matchesQuery(r)
      )
      .sort((a, b) => a.role_name.localeCompare(b.role_name));

    return [...selected, ...unselected];
  }, [
    roles,
    selectedRoles,
    searchTerm,
    preserveItemOrder,
    reorderSelectedOnOutside,
    selectedFirstMode,
    selectedSnapshot,
  ]);

  const toggleRole = (roleId: number) => {
    if (disabled) return;

    if (selectedRoles.includes(roleId)) {
      onSelectionChange(selectedRoles.filter((id) => id !== roleId));
    } else {
      onSelectionChange([...selectedRoles, roleId]);
    }
  };

  return (
    <div ref={wrapperRef} className="space-y-4">
      <div className="relative">
        <FaSearch className="absolute top-3 left-3 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 pl-10 border border-gray-300 rounded-lg bg-gray-50 text-sm"
          placeholder="Search Roles"
        />
      </div>

      <div className="bg-white p-4 rounded-lg border min-h-48 max-h-80 overflow-y-auto">
        {filteredRoles.map((role) => (
          <div
            key={role.role_id}
            className="flex justify-between items-center py-2 hover:bg-blue-50 rounded cursor-pointer"
            onClick={() => toggleRole(role.role_id)}
          >
            <span className="text-sm">{role.role_name}</span>
            <input
              type="checkbox"
              checked={selectedRoles.includes(role.role_id)}
              onChange={() => toggleRole(role.role_id)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RolesSelector;